import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../LangContext";
import { Calendar, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = {
    EN: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
    FR: ["JAN", "FÉV", "MAR", "AVR", "MAI", "JUN", "JUL", "AOÛ", "SEP", "OCT", "NOV", "DÉC"],
    NL: ["JAN", "FEB", "MRT", "APR", "MEI", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEC"],
};

function parseDate(iso, langMonths) {
    if (!iso) return { day: "??", month: "", year: "" };
    const d = new Date(iso);
    if (isNaN(d)) return { day: "??", month: "", year: "" };
    return {
        day: String(d.getDate()).padStart(2, "0"),
        month: langMonths[d.getMonth()] || "",
        year: String(d.getFullYear()),
    };
}

export default function Events() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);
    const monthNames = MONTHS[lang] || MONTHS.EN;

    // Multilingual field picker with EN fallback (tolerates legacy plain strings)
    const pick = (f) => (f && typeof f === "object" ? (f[lang.toLowerCase()] || f.en || "") : f || "");

    useEffect(() => {
        axios.get(`${API}/news?kind=event`).then((r) => setItems(r.data)).catch(() => {});
    }, []);

    if (!items.length) return null;

    return (
        <section id="events" data-testid="events-section" className="py-24 md:py-32 px-6 md:px-12 bg-ink">
            <div className="max-w-7xl mx-auto">
                <div className="mb-16 max-w-3xl">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.events.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {t.events.title}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                    {items.map((e, i) => {
                        const d = parseDate(e.date, monthNames);
                        return (
                            <Link
                                key={e.id}
                                to={`/news/${e.slug}`}
                                data-testid={`event-card-${i}`}
                                className="bg-inksurface group flex flex-col md:flex-row hover:bg-ink transition-colors relative overflow-hidden min-h-[280px]"
                            >
                                {/* Date block */}
                                <div className="md:w-44 flex-shrink-0 bg-ink border-r border-white/5 flex flex-col items-center justify-center p-6 md:p-0 relative">
                                    <div className="font-graffiti text-6xl md:text-7xl text-volt leading-none">{d.day}</div>
                                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-white mt-2">{d.month}</div>
                                    <div className="font-mono text-[10px] text-zinc-500 mt-1">{d.year}</div>
                                    <div className="absolute top-3 left-3 text-[9px] font-mono uppercase tracking-wider text-volt border border-volt/40 px-2 py-1 flex items-center gap-1">
                                        <Calendar size={9} /> {t.events.upcoming}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 p-6 md:p-8 flex flex-col relative">
                                    {e.image_url && (
                                        <>
                                            <div
                                                className="absolute inset-0 bg-cover bg-center opacity-15 group-hover:opacity-25 transition-opacity"
                                                style={{ backgroundImage: `url(${e.image_url})` }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-r from-inksurface via-inksurface/80 to-transparent group-hover:from-ink group-hover:via-ink/80" />
                                        </>
                                    )}
                                    <div className="relative flex-1 flex flex-col">
                                        <h3 className="font-graffiti text-2xl md:text-3xl text-white leading-tight tracking-tight">
                                            {pick(e.title)}
                                        </h3>
                                        <p className="mt-4 text-sm text-zinc-400 leading-relaxed flex-1">{pick(e.excerpt)}</p>
                                        <span className="mt-6 text-xs uppercase tracking-wider text-zinc-300 group-hover:text-volt inline-flex items-center gap-2">
                                            {t.events.read_more} <ArrowUpRight size={14} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
